import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  CheckCircle, 
  AlertTriangle, 
  Download, 
  Shield, 
  Calendar,
  User,
  Mail,
  MapPin,
  Clock,
  Hash,
  FileText,
  Award,
  Eye,
  Smartphone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'https://hr-app-fixes-1.preview.emergentagent.com';

const PublicValidationPage = () => {
  const { validationCode } = useParams();
  const [validationData, setValidationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputCode, setInputCode] = useState(validationCode || '');
  const [showDetails, setShowDetails] = useState(false);

  const validateDocument = async (code) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/api/validate/${code}`);
      setValidationData(response.data);
      setShowDetails(true);
      
      toast.success('Documento validado com sucesso!');
      
    } catch (error) {
      console.error('Erro na validação:', error);
      
      if (error.response?.status === 404) {
        setError('Código de validação não encontrado');
      } else {
        setError('Erro ao validar documento. Tente novamente.');
      }
      
      toast.error('Erro na validação do documento');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async () => {
    try {
      const response = await axios.get(
        `${API}/api/validate/${inputCode}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${validationData.document.title}_VALIDADO.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download iniciado!');
      
    } catch (error) {
      console.error('Erro no download:', error);
      toast.error('Erro ao fazer download do documento');
    }
  };

  const downloadCertificate = async () => {
    try {
      const response = await axios.get(`${API}/api/validate/${inputCode}/certificate`);
      
      // Criar PDF do certificado (implementação simplificada)
      const certificateContent = JSON.stringify(response.data, null, 2);
      const blob = new Blob([certificateContent], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado_${inputCode}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificado baixado!');
      
    } catch (error) {
      console.error('Erro ao baixar certificado:', error);
      toast.error('Erro ao baixar certificado');
    }
  };

  const formatDateTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    if (validationCode) {
      validateDocument(validationCode);
    } else {
      setLoading(false);
    }
  }, [validationCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Validando documento...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Validação de Documento Digital
          </h1>
          <p className="text-gray-600">
            Sistema de verificação de autenticidade e integridade - GridProfile
          </p>
        </div>

        {/* Formulário de Validação */}
        {!showDetails && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Eye className="h-5 w-5 mr-2" />
                Inserir Código de Validação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="validation-code">Código de Validação</Label>
                <Input
                  id="validation-code"
                  value={inputCode}
                  onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                  placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                  className="font-mono text-center"
                  maxLength={23}
                />
                <p className="text-sm text-gray-500 text-center">
                  Digite ou cole o código de validação de 20 caracteres
                </p>
              </div>
              
              {error && (
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertTriangle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}
              
              <Button 
                onClick={() => validateDocument(inputCode)}
                disabled={!inputCode || inputCode.length < 20}
                className="w-full"
                size="lg"
              >
                <Shield className="h-5 w-5 mr-2" />
                Validar Documento
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Resultado da Validação */}
        {validationData && (
          <>
            {/* Status de Validação */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center justify-center space-x-4 mb-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-green-600 mb-1">
                      DOCUMENTO VÁLIDO
                    </h2>
                    <p className="text-gray-600">
                      Assinaturas digitais verificadas e íntegras
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <Shield className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="font-semibold text-green-800">Autêntico</p>
                    <p className="text-sm text-green-600">Não modificado</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Award className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <p className="font-semibold text-blue-800">Válido Juridicamente</p>
                    <p className="text-sm text-blue-600">MP 2.200-2/2001</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <User className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <p className="font-semibold text-purple-800">{validationData.signatures.length} Assinatura(s)</p>
                    <p className="text-sm text-purple-600">Verificada(s)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Informações do Documento */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Informações do Documento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Título</Label>
                    <p className="font-semibold">{validationData.document.title}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Status</Label>
                    <Badge variant={validationData.document.status === 'signed' ? 'default' : 'secondary'}>
                      {validationData.document.status === 'signed' ? 'Assinado' : validationData.document.status}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Criado em</Label>
                    <p>{formatDateTime(validationData.document.created_at)}</p>
                  </div>
                  {validationData.document.signed_at && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Assinado em</Label>
                      <p>{formatDateTime(validationData.document.signed_at)}</p>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-gray-500">Hash do Documento Original</Label>
                    <p className="font-mono text-xs bg-gray-100 p-2 rounded break-all">
                      {validationData.document.original_hash}
                    </p>
                  </div>
                  {validationData.document.signed_hash && (
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium text-gray-500">Hash do Documento Assinado</Label>
                      <p className="font-mono text-xs bg-gray-100 p-2 rounded break-all">
                        {validationData.document.signed_hash}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Timeline de Assinaturas */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Timeline de Assinaturas ({validationData.signatures.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {validationData.signatures.map((signature, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 pb-4">
                      <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-lg">{signature.signer_name}</h4>
                            <div className="flex items-center text-sm text-gray-600 mt-1">
                              <Mail className="h-4 w-4 mr-1" />
                              {signature.signer_email}
                            </div>
                          </div>
                          <Badge variant="outline">
                            Assinatura #{index + 1}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {signature.signer_cpf && (
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-2 text-gray-400" />
                              <span>CPF: {signature.signer_cpf}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                            <span>{formatDateTime(signature.signed_at)}</span>
                          </div>
                          
                          {signature.signature_ip && (
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                              <span>IP: {signature.signature_ip}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center">
                            <Smartphone className="h-4 w-4 mr-2 text-gray-400" />
                            <span>Método: {signature.signature_method || 'digital'}</span>
                          </div>
                        </div>
                        
                        <div className="mt-3 flex items-center">
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            ✓ Nível: {signature.authentication_level || 'basic'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Informações de Validação */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Hash className="h-5 w-5 mr-2" />
                  Informações desta Validação
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Código de Validação</Label>
                    <p className="font-mono">{validationData.validation_info.validation_code}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Validado em</Label>
                    <p>{formatDateTime(validationData.validation_info.validated_at)}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">IP do Validador</Label>
                    <p>{validationData.validation_info.validator_ip}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Total de Assinaturas</Label>
                    <p>{validationData.validation_info.total_signatures}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Conformidade Legal */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Award className="h-5 w-5 mr-2" />
                  Conformidade Legal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-green-800 mb-2">
                        Documento em Conformidade Legal
                      </h4>
                      <div className="space-y-2 text-sm text-green-700">
                        <p>✓ Assinaturas válidas conforme {validationData.legal_compliance.law_reference}</p>
                        <p>✓ Integridade do documento verificada: {validationData.legal_compliance.integrity_verified ? 'Sim' : 'Não'}</p>
                        <p>✓ Validade jurídica das assinaturas confirmada</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ações */}
            <Card>
              <CardHeader>
                <CardTitle>Ações Disponíveis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4">
                  <Button onClick={downloadDocument} className="flex-1 md:flex-none">
                    <Download className="h-4 w-4 mr-2" />
                    Baixar Documento
                  </Button>
                  
                  <Button onClick={downloadCertificate} variant="outline" className="flex-1 md:flex-none">
                    <Award className="h-4 w-4 mr-2" />
                    Baixar Certificado
                  </Button>
                  
                  <Button 
                    onClick={() => {
                      setShowDetails(false);
                      setValidationData(null);
                      setInputCode('');
                    }}
                    variant="outline"
                    className="flex-1 md:flex-none"
                  >
                    Nova Validação
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Sistema de Validação Digital - GridProfile
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Desenvolvido em conformidade com a MP 2.200-2/2001
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicValidationPage;